import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import InputWithLabel from "../../components/form/input/InputWithLabel";
import AdminFormPage from "../../components/admin/AdminFormPage";
import { adminFormFullWidthClass, adminFormGridClass } from "../../components/admin/adminStyles";
import { FormInputEvent } from "../../types/form";

interface Role {
  id: string;
  roleName: string;
  roleDescription: string;
}

export default function RoleForm() {
  const { roleId } = useParams<{ roleId?: string }>();
  const navigate = useNavigate();

  const [role, setRole] = useState<Role>({
    id: "",
    roleName: "",
    roleDescription: "",
  });

  const [loading, setLoading] = useState(Boolean(roleId));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!roleId) return;
    setLoading(true);
    axiosInstance
      .get(`/Role/get/${roleId}`)
      .then((response) => {
        setRole(response.data);
      })
      .catch((err) => {
        setError("Failed to load role data.");
        console.error("Error fetching role:", err);
      })
      .finally(() => setLoading(false));
  }, [roleId]);

  const handleChange = (e: FormInputEvent) => {
    const { name, value } = e.target;
    setRole((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!role.roleName || !role.roleDescription) {
      setError("Role Name and Description are required.");
      return;
    }

    setSaving(true);
    try {
      if (roleId) {
        await axiosInstance.put(`/Role/update/${roleId}`, role);
      } else {
        await axiosInstance.post(`/Role/add`, role);
      }
      navigate("/role");
    } catch (err) {
      setError("Error saving role. Please try again.");
      console.error("Save error:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminFormPage
      pageTitle={roleId ? "Edit Role" : "Add Role"}
      description={roleId ? "Update role details and permissions scope" : "Create a new admin role"}
      error={error}
      loading={loading}
      onSubmit={handleSubmit}
      listRoute="/role"
      saving={saving}
      saveLabel={roleId ? "Update Role" : "Add Role"}
    >
      <div className={adminFormGridClass}>
        <InputWithLabel
          label="Role Name"
          name="roleName"
          value={role.roleName}
          onChange={handleChange}
          required
          placeholder="Enter role name"
        />
        <div className={adminFormFullWidthClass}>
          <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Role Description <span className="text-error-500">*</span>
          </label>
          <textarea
            name="roleDescription"
            value={role.roleDescription}
            onChange={handleChange}
            placeholder="Enter role description"
            rows={5}
            required
            className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm shadow-theme-xs focus:border-brand-300 focus:outline-none focus:ring-2 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
          />
        </div>
      </div>
    </AdminFormPage>
  );
}
